<template>
  <!-- Notifications are handled by vue-toastification -->
  <div />
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { wsConnection } from '@/services/api'

const toast = useToast()

interface Notification {
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
}

function handleNotification(notification: Notification) {
  const content = notification.title 
    ? `${notification.title}: ${notification.message}`
    : notification.message

  switch (notification.type) {
    case 'success':
      toast.success(content)
      break
    case 'error':
      toast.error(content)
      break
    case 'warning':
      toast.warning(content)
      break
    case 'info':
    default:
      toast.info(content)
  }
}

function handleServerAlert(data: { server: string; status: string; message?: string }) {
  if (data.status === 'offline') {
    toast.error(`Servidor ${data.server} está offline!`, {
      timeout: 10000,
    })
  } else if (data.status === 'degraded') {
    toast.warning(`Servidor ${data.server} com performance degradada`, {
      timeout: 8000,
    })
  } else if (data.status === 'online') {
    toast.success(`Servidor ${data.server} está online novamente`)
  }
}

function handleAnomaly(data: { type: string; severity: string; message: string }) {
  const severityColors = {
    critical: 'error',
    high: 'error',
    medium: 'warning',
    low: 'info',
  } as const

  const toastType = severityColors[data.severity as keyof typeof severityColors] || 'info'
  
  toast[toastType](`🚨 ${data.message}`, {
    timeout: data.severity === 'critical' ? 0 : 10000, // Critical stays until dismissed
  })
}

onMounted(() => {
  wsConnection.on('notification', handleNotification)
  wsConnection.on('server:alert', handleServerAlert)
  wsConnection.on('anomaly:detected', handleAnomaly)
})

onUnmounted(() => {
  wsConnection.off('notification', handleNotification)
  wsConnection.off('server:alert', handleServerAlert)
  wsConnection.off('anomaly:detected', handleAnomaly)
})
</script>
